import React, { useState, useEffect } from "react";
import "./search.css";
import TaxonomyTree from "../taxonomyTree/TaxonomyTree";
import BrandList from "../brandList/BrandList";
import LoadingSpinner from "../loadingSpinner/LoadingSpinner";
import { fetchWithAuth } from "../../utils/fetchWithAuth";
import { hasDbEditorUpdateAccessFromStorage } from "../../utils/authAccess";

const saveSession = (key, value) =>
  sessionStorage.setItem(key, JSON.stringify(value));
const loadSession = (key, fallback) => {
  const value = sessionStorage.getItem(key);
  return value ? JSON.parse(value) : fallback;
};

export default function Search({
  setSelectedTab,
  setPartData,
  setModelData,
  setDocumentData,
  setPartByModelData,
  setImageData,
}) {
  const [searchBy, setSearchBy] = useState(() =>
    loadSession("searchBy", "model")
  );
  const [searchValue, setSearchValue] = useState(() =>
    loadSession("searchValue", "")
  );
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({
    name: "",
    div: "",
  });
  const [selectedTaxonomyId, setSelectedTaxonomyId] = useState(() =>
    loadSession("taxonomyId", null)
  );
  const [taxonomyData, setTaxonomyData] = useState([]);
  const [brandList, setBrandList] = useState(() =>
    loadSession("brandList", [])
  );
  const [selectedBrandId, setSelectedBrandId] = useState(() =>
    loadSession("brandId", null)
  );
  const [expandedIds, setExpandedIds] = useState(() =>
    loadSession("expandedTaxonomyIds", [])
  );
  const [loading, setLoading] = useState(false);
  const [popupVisible, setPopupVisible] = useState(false);
  const [originalFormData, setOriginalFormData] = useState(null);
  const [isNoPartsFoundModalOpen, setIsNoPartsFoundModalOpen] = useState(false);
  const [isNoModelFoundModalOpen, setIsNoModelFoundModalOpen] = useState(false);
  const username = localStorage.getItem("username") || "DBEditorWeb";
  const [formData, setFormData] = useState({
    id: "",
    name: "",
    div: "",
  });

  useEffect(() => {
    fetchTaxonomyData();
  }, []);

  useEffect(() => saveSession("searchBy", searchBy), [searchBy]);
  useEffect(() => saveSession("searchValue", searchValue), [searchValue]);
  useEffect(
    () => saveSession("taxonomyId", selectedTaxonomyId),
    [selectedTaxonomyId]
  );
  useEffect(() => saveSession("brandList", brandList), [brandList]);
  useEffect(() => saveSession("brandId", selectedBrandId), [selectedBrandId]);
  useEffect(
    () => saveSession("expandedTaxonomyIds", expandedIds),
    [expandedIds]
  );

  const fetchTaxonomyData = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchWithAuth({
        method: "GET",
        endpoint: "/taxonomy/tree",
      });
      setTaxonomyData(data || []);
      return data;
    } catch (err) {
      console.error("Error fetching taxonomy data:", err);
      setError("Failed to load taxonomy data.");
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newFieldErrors = {
      name: "",
      div: "",
    };
    let isValid = true;

    if (!formData.name.trim()) {
      newFieldErrors.name = "Description cannot be empty";
      isValid = false;
    }

    if (!formData.div.trim()) {
      newFieldErrors.div = "Division cannot be empty";
      isValid = false;
    } else if (!/^\d+$/.test(formData.div)) {
      newFieldErrors.div = "Division must be numeric (digits only)";
      isValid = false;
    } else if (formData.div.trim().length !== 4) {
      newFieldErrors.div = "Division must be exactly 4 digits";
      isValid = false;
    }

    setFieldErrors(newFieldErrors);
    return isValid;
  };

  const findNodeById = (nodes, id) => {
    for (const node of nodes) {
      if (node.id === id) {
        return node;
      }
      if (node.children) {
        const childNode = findNodeById(node.children, id);
        if (childNode) {
          return childNode;
        }
      }
    }
    return null;
  };

  const handleTaxonomySelect = (id) => {
    const selectedItem = findNodeById(taxonomyData, id);
    setSelectedTaxonomyId(id);
    setSelectedBrandId(null);
    setModelData([]);
    setDocumentData([]);
    setPartByModelData([]);
    setImageData([]);
    setError("");
    setFormData({
      id: selectedItem ? selectedItem.id : "",
      name: selectedItem ? selectedItem.name : "",
      div: selectedItem ? selectedItem.div : "",
    });
  };

  const handleTaxonomyDoubleClick = async (id) => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchWithAuth({
        method: "GET",
        endpoint: `/brands/by-taxonomy/${id}`,
      });
      setBrandList(data || []);
      setSelectedBrandId(null);
      setModelData([]);
      setDocumentData([]);
      setPartByModelData([]);
      setImageData([]);
      setPartData([]);
      setError("");
    } catch (err) {
      console.error("Error fetching brand list:", err);
      setBrandList([]);
      setError("Failed to load brand list.");
    } finally {
      setLoading(false);
    }
  };

  const handleBrandClick = async (brand) => {
    setSelectedBrandId(brand.id);
    setLoading(true);
    setError("");
    try {
      const data = await fetchWithAuth({
        method: "GET",
        endpoint: `/model/by-taxonomy-brand`,
        params: {
          taxonomyId: selectedTaxonomyId,
          brandId: brand.name,
        },
      });
      setModelData(data || []);
      setSelectedTab("MODELS");
    } catch (err) {
      console.error("Error fetching models:", err);
      setModelData([]);
      setError("Failed to load models.");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    // Clear previous data and error
    setPartData([]);
    setModelData([]);
    setDocumentData([]);
    setPartByModelData([]);
    setImageData([]);
    setError("");
    setIsNoModelFoundModalOpen(false);

    if (searchBy === "part") {
      if (searchValue.trim().length < 3) {
        setError("Minimum 3 characters required for Part search");
        return;
      }
      setLoading(true);
      try {
        const data = await fetchWithAuth({
          method: "GET",
          endpoint: "/part/getPartByNumber",
          params: { number: searchValue.trim() },
        });
        if (Array.isArray(data) && data.length > 0) {
          setPartData(data);
          setSelectedTab("PARTS");
        } else {
          setIsNoPartsFoundModalOpen(true);
        }
      } catch (err) {
        console.error("Error searching part:", err);
        setError("Something went wrong during Part search.");
      } finally {
        setLoading(false);
      }
    } else if (searchBy === "model") {
      if (searchValue.trim().length < 3) {
        setError("Minimum 3 characters required for Model search");
        return;
      }
      setLoading(true);
      try {
        const modelData = await fetchWithAuth({
          method: "GET",
          endpoint: "/model/getModelByNumber",
          params: { number: searchValue.trim() },
        });

        // Check if no models found
        if (!modelData || !Array.isArray(modelData) || modelData.length === 0) {
          setIsNoModelFoundModalOpen(true);
          return;
        }
       
        // If more than 1 model found - show models tab
        if (modelData.length >= 1) {
          setModelData(modelData);
          setSelectedTab("MODELS");
          return;
        }

        // Single model found - proceed with document and part checks
        const docData = await fetchWithAuth({
          method: "GET",
          endpoint: "/model/getDocumentByModelNumber",
          params: { hsin: modelData[0].hsin },
        });

        // Check if no documents found
        if (!docData || !Array.isArray(docData) || docData.length === 0) {
          setError("No Document Found.");
          return;
        }

        if (docData.length > 1) {
          setModelData(modelData);
          setDocumentData(docData);
          setSelectedTab("DOCUMENTS");
          setPartByModelData([]);
          setPartData([]);

        } else if (docData.length === 1) {
          const partData = await fetchWithAuth({
            method: "GET",
            endpoint: "/model/getPartAndPagesByModel",
            params: { modelNumber: searchValue.trim(), documentId: docData[0].documentId },
          });

          let enrichedPartData = partData;
          if (Array.isArray(partData) && partData.length > 0 && modelData && modelData.length > 0) {
            const modelInfo = modelData[0];
            enrichedPartData = [...partData];
            if (enrichedPartData[0]) {
              enrichedPartData[0] = {
                ...enrichedPartData[0],
                model: modelInfo.number || modelInfo.modelNumber || modelInfo.model || searchValue.trim(),
                brndName: modelInfo.brandName || modelInfo.brndName || "",
                prdName: modelInfo.productType || modelInfo.productName || modelInfo.prdName || "",
                documentId: docData[0].documentId, // Store documentId in the data for later use
              };
            }
          } else if (Array.isArray(partData) && partData.length > 0) {
            // Even if modelData is empty, still add documentId to the first item
            enrichedPartData = [...partData];
            if (enrichedPartData[0]) {
              enrichedPartData[0] = {
                ...enrichedPartData[0],
                documentId: docData[0].documentId, // Store documentId in the data for later use
              };
            }
          }

            if (Array.isArray(enrichedPartData) && enrichedPartData.length > 0 && 
                enrichedPartData.some(item => item.partNumber != null || item.number != null)) {
              setModelData(modelData);
              setSelectedTab("PARTS");
              setDocumentData(docData);
              setPartByModelData(enrichedPartData);
              setImageData(enrichedPartData);
              setPartData([]);
              return;
          }

          setImageData(enrichedPartData);
          setSelectedTab("IMAGE");
          setPartByModelData([]);
          setPartData([]);
          setDocumentData(docData);
        }
      } catch (err) {
        console.error("Error searching model:", err);
        setError("No Model Found");
      } finally {
        setLoading(false);
      }
    }
  };


  const handleClear = () => {
    setSearchValue("");
    setError("");
    setIsNoModelFoundModalOpen(false);
    setFieldErrors({ name: "", div: "" });
    setModelData([]);
    setDocumentData([]);
    setPartData([]);
    setPartByModelData([]);
    setSelectedTaxonomyId(null);
    setSelectedBrandId(null);
    setBrandList([]);
    setExpandedIds([]);

    sessionStorage.removeItem("searchValue");
    sessionStorage.removeItem("taxonomyId");
    sessionStorage.removeItem("brandId");
    sessionStorage.removeItem("brandList");
    sessionStorage.removeItem("expandedTaxonomyIds");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !loading) {
      handleSearch();
    }
  };

  const isFormChanged = () => {
    return JSON.stringify(formData) !== JSON.stringify(originalFormData);
  };

  const handleUpdateProductClick = () => {
    setPopupVisible(true);
    setOriginalFormData(formData);
    setFieldErrors({ name: "", div: "" }); // Clear any existing field errors
    setError(""); // Clear any existing general errors
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    
    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors((prev) => ({ ...prev, [name]: "" }));
    }
    
    // Clear general error when user starts typing
    if (error) {
      setError("");
    }
  };

  const handleUpdate = async () => {
    if (!validateForm()) {
      setFormData(originalFormData);
      return;
    }

    const payload = {
      name: formData.name.trim(),
      div: formData.div.trim(),
      updatedBy: username,
    };

    setLoading(true);

    try {
      await fetchWithAuth({
        method: "PUT",
        endpoint: `/taxonomy/update/${formData.id}`,
        headers: { "Content-Type": "application/json" },
        data: payload,
      });

      const updatedTree = await fetchTaxonomyData();

      const updatedNode = findNodeById(updatedTree, formData.id);

      if (updatedNode) {
        setFormData({
          id: updatedNode.id,
          name: updatedNode.name,
          div: updatedNode.div,
        });
        setOriginalFormData({
          id: updatedNode.id,
          name: updatedNode.name,
          div: updatedNode.div,
        });
      }

      setPopupVisible(false);
    } catch (err) {
      console.error("Error updating Product Type:", err);

      if (err.response && err.response.data) {
        const errorData = err.response.data;
        const newFieldErrors = { name: "", div: "" };
        
        // Parse field-specific errors from API response
        if (errorData.errors) {
          // Handle validation errors object
          if (errorData.errors.name) {
            newFieldErrors.name = Array.isArray(errorData.errors.name) 
              ? errorData.errors.name[0] 
              : errorData.errors.name;
          }
          if (errorData.errors.div || errorData.errors.division) {
            newFieldErrors.div = Array.isArray(errorData.errors.div || errorData.errors.division) 
              ? (errorData.errors.div || errorData.errors.division)[0] 
              : (errorData.errors.div || errorData.errors.division);
          }
        } else if (errorData.message || errorData.error) {
          // Handle general error message - try to parse field-specific info
          const errorMessage = errorData.message || errorData.error;
          
          // Check if error message contains field-specific information
          if (errorMessage.toLowerCase().includes('division') || 
              errorMessage.toLowerCase().includes('div') ||
              errorMessage.toLowerCase().includes('division code') ||
              errorMessage.toLowerCase().includes('division field')) {
            newFieldErrors.div = errorMessage;
          } else if (errorMessage.toLowerCase().includes('description') || 
                     errorMessage.toLowerCase().includes('name') ||
                     errorMessage.toLowerCase().includes('product name') ||
                     errorMessage.toLowerCase().includes('product description')) {
            newFieldErrors.name = errorMessage;
          } else {
            // If we can't determine the field, show as general error
            setError(errorMessage);
          }
        } else if (errorData.validationErrors) {
          // Handle alternative validation errors format
          if (errorData.validationErrors.name) {
            newFieldErrors.name = errorData.validationErrors.name;
          }
          if (errorData.validationErrors.div || errorData.validationErrors.division) {
            newFieldErrors.div = errorData.validationErrors.div || errorData.validationErrors.division;
          }
        } else {
          setError("Failed to update Product Type.");
        }
        
        setFieldErrors(newFieldErrors);
      } else if (err.request) {
        setError("A network or server error occurred.");
      } else {
        setError("Unexpected error occurred while updating Product Type.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setPopupVisible(false);
    setFormData(originalFormData);
  };

  return (
    <>
      {searchBy !== "product" && (
        <div className="searchBlock">
          <div className="searchTabContainer">
            <div className="formGroup">
              <label htmlFor="searchBySelect">Search By</label>
              <select
                id="searchBySelect"
                value={searchBy}
                onChange={(e) => {
                  const value = e.target.value;
                  setSearchBy(value);
                  setSearchValue("");
                  setError("");
                  if (value !== "product") {
                    setSelectedTaxonomyId(null);
                    setSelectedBrandId(null);
                    setBrandList([]);
                    setExpandedIds([]);
                  } else {
                    setModelData([]);
                    setDocumentData([]);
                    setPartByModelData([]);
                    setImageData([]);
                    setPartData([]);
                  }
                }}
              >
                <option value="model">Model</option>
                <option value="product">Product/Brand</option>
                <option value="part">Part Number</option>
              </select>
            </div>

            <div className="formGroup">
              <label htmlFor="searchInput">Enter {searchBy}</label>
              <input
                type="text"
                id="searchInput"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onKeyDown={handleKeyDown}
              />
            </div>

            <div className="buttonGroup">
              <button onClick={handleSearch} disabled={loading}>
                Search
              </button>
              <button onClick={handleClear} disabled={loading}>
                Clear
              </button>
            </div>
          </div>
        </div>
      )}

      {searchBy === "product" && (
        <div className="mainProductContent">
          <div className="leftPanel">
            <div className="searchTabContainer">
              <div className="formGroup">
                <label htmlFor="searchBySelectProduct">Search By</label>
                <select
                  id="searchBySelectProduct"
                  value={searchBy}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSearchBy(value);
                    setSearchValue("");
                    setError("");
                    if (value !== "product") {
                      setSelectedTaxonomyId(null);
                      setSelectedBrandId(null);
                      setBrandList([]);
                      setExpandedIds([]);
                    }
                  }}
                >
                  <option value="model">Model</option>
                  <option value="product">Product/Brand</option>
                  <option value="part">Part Number</option>
                </select>
              </div>

              <div className="buttonGroup">
                <button
                  className="updateProductButton"
                  onClick={handleUpdateProductClick}
                  disabled={
                    !selectedTaxonomyId ||
                    !hasDbEditorUpdateAccessFromStorage()
                  }
                >
                  Update Product Type
                </button>
              </div>
            </div>
          </div>

          <div className="middlePanel taxonomyBlock">
            <div className="taxonomy-header">Select a Product or Brand</div>
            {loading ? (
              <LoadingSpinner />
            ) : (
              <TaxonomyTree
                data={taxonomyData}
                selectedId={selectedTaxonomyId}
                onSelect={handleTaxonomySelect}
                onDoubleClick={handleTaxonomyDoubleClick}
                expandedIds={expandedIds}
                setExpandedIds={setExpandedIds}
              />
            )}
          </div>

          <div className="rightPanel modelBlockContainer">
            {brandList.length > 0 && (
              <BrandList
                brands={brandList}
                onBrandClick={handleBrandClick}
                selectedBrandId={selectedBrandId}
                isLoading={loading}
              />
            )}
          </div>
        </div>
      )}

      {popupVisible && (
        <div className="popup-overlay">
          <div className="popup-content">
            <h3>Update Product Type</h3>
            <form>
              <div className="formGroup">
                <label htmlFor="id">Product Type:</label>
                <input
                  type="text"
                  id="id"
                  name="id"
                  value={formData.id}
                  readOnly
                  disabled
                />
              </div>
              <div className="formGroup">
                <label htmlFor="name">Description:</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleFormChange}
                  className={fieldErrors.name ? "input-error" : ""}
                />
                {fieldErrors.name && (
                  <div className="field-error">{fieldErrors.name}</div>
                )}
              </div>
              <div className="formGroup">
                <label htmlFor="div">Division:</label>
                <input
                  type="text"
                  id="div"
                  name="div"
                  value={formData.div}
                  maxLength={4}
                  onChange={handleFormChange}
                  className={fieldErrors.div ? "input-error" : ""}
                />
                {fieldErrors.div && (
                  <div className="field-error">{fieldErrors.div}</div>
                )}
              </div>
              <div className="buttonGroup">
                <button
                  type="button"
                  onClick={handleUpdate}
                  disabled={!isFormChanged()}
                >
                  Update
                </button>
                <button type="button" onClick={handleCancel}>
                  Cancel
                </button>
              </div>
            </form>
            {error && (
              <div className="general-error">
                <div className="error-message">{error}</div>
                <button 
                  className="error-close" 
                  onClick={() => setError("")}
                >
                  &times;
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {loading && (
        <>
          <div className="blur-background"></div>
          <div className="loading"></div>
        </>
      )}

      {isNoPartsFoundModalOpen && (
        <div className="popup-overlay">
          <div className="popup-content">
            <h3>No Parts Found</h3>
            <p>
              No parts were found for the search term "{searchValue}".
            </p>
            <div className="buttonGroup">
              <button onClick={() => setIsNoPartsFoundModalOpen(false)}>
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {isNoModelFoundModalOpen && (
        <div className="popup-overlay">
          <div className="popup-content">
            <h3>No Model Found</h3>
            <p>
              No model was found for the search term "{searchValue}".
            </p>
            <div className="buttonGroup">
              <button onClick={() => setIsNoModelFoundModalOpen(false)}>
                OK
              </button>
            </div>
          </div>
        </div>
      )}

    </>
  );
}
